<script setup lang="ts">
import CardHeading from "../CardHeading.vue";
// Vue
import { ref, computed, onMounted, watch, type WritableComputedRef } from "vue";
import { ChevronDown, PenLine, SlidersHorizontal } from "@lucide/vue";

// Data Providers
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";

// Types
import { WizardProjectType } from "@/features/BuildingProject";
import type { ProjectRevisionScalars } from "@/pages/ProjectVersionHistory/types/projectRevision.types";

// Utils
import { BuildingProjectStageEnums } from "@/enums";
import { PROJECT_NAME_TAKEN_MESSAGE, useProjectNameAvailability } from "@/composables/useProjectNameAvailability.composable";

// Components

import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import YearInput from "@/components/Reusables/Inputs/YearInput.vue";
import ModalConfirm, { useModalConfirm } from "@/components/Reusables/Modal/ModalConfirm";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import StepIntro from "../StepIntro/StepIntro.vue";

const props = defineProps<{
  wasValidated: boolean;
  project: WizardProjectType;
}>();

const emit = defineEmits<{
  /** The duplicate-name check's answer, for the settings page's single Save button, which can't
   *  see this field's state directly; unlistened elsewhere (e.g. the create wizard) costs nothing. */
  "name-taken": [taken: boolean];
}>();

const modalConfirm = useModalConfirm();
const authStateStore = useAuthStateStore();

// One write path, and no copy of the project: see composables/useWizardProject.ts for why the
// `ref(props.project)` this replaces was both lint evasion and a latent staleness bug.
const { isExistingProject, hasEmissionFactors, resetEmissionFactors, field, lens, projectId } = useWizardProject(
  () => props.project
);
const revisionsStore = useProjectRevisionsStore();

/** The scalars a PREVIEWED revision holds, or null for live — without this, the step showed
 *  today's values under an older revision's banner, since project scalars have no as-of row. */
const previewedScalars = computed<ProjectRevisionScalars | null>(() =>
  projectId.value && revisionsStore.isPreviewing(projectId.value) ? revisionsStore.readProjectScalarsFor(projectId.value) : null
);

/** True while the step is showing a revision's frozen scalars instead of the live, editable ones. */
const isReadOnly = computed(() => previewedScalars.value !== null);

/** Shows the previewed revision's value; the setter is inert while previewing, and a field the
 *  revision has no value for falls back to live rather than blanking. */
const asOf = <T,>(live: WritableComputedRef<T>, pick: (scalars: ProjectRevisionScalars) => T | null | undefined) =>
  computed<T>({
    get: () => {
      const scalars = previewedScalars.value;
      if (!scalars) return live.value;
      const value = pick(scalars);
      return (value ?? live.value) as T;
    },
    set: (value: T) => {
      if (isReadOnly.value) return;
      live.value = value;
    },
  });

const projectName = field("projectName");
const projectNumber = asOf(field("projectNumber"), (scalars) => scalars.projectNumber);
const buildingProjectStageEnum = asOf(field("buildingProjectStageEnum"), (scalars) => scalars.projectStatusId);
const numberOfPeople = asOf(field("numberOfPeople"), (scalars) => scalars.numberOfPeople);
const builder = asOf(field("builder"), (scalars) => scalars.builderName);
const clientName = asOf(field("clientName"), (scalars) => scalars.clientName);
const lcaResponsible = asOf(field("lcaResponsibleName"), (scalars) => scalars.lcaResponsibleName);

const yearOfConstruction = asOf(
  lens(
    (current) => current.emissionCalculationAssumptions.emissionMetaData.yearOfConstruction,
    (current, value) => (current.emissionCalculationAssumptions.emissionMetaData.yearOfConstruction = value)
  ),
  (scalars) => scalars.yearOfConstruction
);

const yearOfCommissioning = asOf(
  lens(
    (current) => current.emissionCalculationAssumptions.emissionMetaData.yearOfCommissioning,
    (current, value) => (current.emissionCalculationAssumptions.emissionMetaData.yearOfCommissioning = value)
  ),
  (scalars) => scalars.yearOfCommissioning
);

/** Skipped only when the name is already answered — an existing project, or the New Project
 *  modal already collected it; otherwise a required field would go unaskable. */
const showProjectName = computed(() => isExistingProject.value || !projectName.value);

/** Same as the name field above; checks `=== null`/`undefined`, not falsiness, since stage 0
 *  ("Competition") is a real answer. */
const showProjectStage = computed(
  () => isExistingProject.value || buildingProjectStageEnum.value === null || buildingProjectStageEnum.value === undefined
);

/** Closed by default: nothing in that section is required, so it starts out of the way. */
const optionalDetailsShown = ref(false);

/** Whether any optional field is filled, distinct from the field-NAME list beside it — the one
 *  thing a collapsed section can't otherwise show. */
const optionalDetailsFilledCount = computed(
  () =>
    [projectNumber.value, numberOfPeople.value, builder.value, clientName.value, lcaResponsible.value].filter(
      (fieldValue) => fieldValue !== null && fieldValue !== undefined && String(fieldValue).trim() !== ""
    ).length
);

const initialProjectName = ref<string>(props.project.projectName);

/** Exempts only the current project's own name (an existing project) — a clone's guessed
 *  `X (Clone)` name isn't owned, so widening this let cloning the same project twice pass silently. */
const { nameExists: projectNameAlreadyExists, check: checkProjectNameAvailability } = useProjectNameAvailability({
  ignoreName: () => (isExistingProject.value ? initialProjectName.value : null),
});

/** `immediate`: a remount must re-state the answer immediately, since the parent's copy starts
 *  false and this component doesn't control that page. */
watch(projectNameAlreadyExists, (taken) => emit("name-taken", taken), { immediate: true });

/** The year waiting on the user's answer to the reset warning, or null when nothing is pending. */
const pendingCommissioningYear = ref<number | null>(null);

const RESET_FACTORS_MODAL_ID = "reset-emission-factors";

// TextInput requires `value: string | null`; lcaResponsibleName is optional (`string | undefined`) on the project model.
const lcaResponsibleName = computed({
  get: () => lcaResponsible.value ?? null,
  set: (value) => {
    lcaResponsible.value = value ?? undefined;
  },
});

const applyCommissioningYear = (year: number) => {
  yearOfCommissioning.value = year;
  resetEmissionFactors();
};

/** Changing this year clears every A5/B6 emission factor already entered — ask first, since
 *  losing a value set steps away without warning would be the worst version of that. */
const handleCommissioningYearSelected = (year: number) => {
  if (!hasEmissionFactors.value) {
    applyCommissioningYear(year);
    return;
  }

  pendingCommissioningYear.value = year;
  modalConfirm.toggleModalConfirm(RESET_FACTORS_MODAL_ID);
};

const handleResetConfirmed = () => {
  if (pendingCommissioningYear.value === null) return;
  applyCommissioningYear(pendingCommissioningYear.value);
  pendingCommissioningYear.value = null;
};

/** Watches the modal closing to drop a cancelled pending year; a confirm already cleared it
 *  synchronously before this flushes, so there's nothing to undo either way. */
watch(
  () => modalConfirm.showModalConfirm(RESET_FACTORS_MODAL_ID),
  (isShowing) => {
    if (isShowing || pendingCommissioningYear.value === null) return;
    pendingCommissioningYear.value = null;
  }
);

/** Pre-fills the signed-in user as LCA responsible on a NEW project only — never overwrites an
 *  existing project's stored value, and the field stays editable either way. */
onMounted(() => {
  if (isExistingProject.value || lcaResponsible.value) return;

  const signedInName = authStateStore.user?.name;
  if (signedInName) lcaResponsible.value = signedInName;
});
</script>

<template>
  <div class="w-full">
    <StepIntro
      class="mb-6"
      heading="What is this project?" />

    <div class="flex flex-col gap-6">
      <div class="rounded-8 bg-slate-0 p-px-20 @container flex flex-col gap-4 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <PenLine
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Basics</CardHeading>
          </div>
          <p class="t-caption text-type-muted">The project's name, status and years.</p>
        </div>

        <!-- Sized by the card (@container), not the viewport — a 10-column row so the name sits at 4/10
             while the other three split 2/10 each, instead of a 12-column row's forced even split. -->
        <fieldset
          class="grid grid-cols-10 gap-4"
          :disabled="isReadOnly">
          <TextInputNew
            v-if="showProjectName"
            v-model="projectName"
            class="col-span-10 min-w-0 @lg:col-span-5 @3xl:col-span-4"
            label="Project name"
            tooltip="A descriptive name used to identify the project within the platform. This helps you and others quickly recognize and distinguish the project from others."
            :error-msg="
              wasValidated && projectName.length <= 0
                ? 'Please enter a valid project name'
                : projectNameAlreadyExists
                  ? PROJECT_NAME_TAKEN_MESSAGE
                  : undefined
            "
            :maxlength="55"
            :remove-leading-whitespace="true"
            :required="true"
            @update:model-value="checkProjectNameAvailability" />

          <SelectInputWithKeyValue
            v-if="showProjectStage"
            v-model:value="buildingProjectStageEnum"
            class="col-span-10 min-w-0 @lg:col-span-5 @3xl:col-span-2"
            :options="
              BuildingProjectStageEnums.getAsArray().map((x) => ({
                name: x.key,
                value: x.value,
              }))
            "
            placeholder="Select Project Status"
            label="Project status"
            tooltip="The current phase of the project, such as design, construction, or completed. This helps structure the workflow and clarifies how the results should be interpreted. Furthermore it is used to keep track of the active square meters in the organization."
            :error-msg="
              wasValidated &&
              (buildingProjectStageEnum === null || buildingProjectStageEnum === undefined || buildingProjectStageEnum < 0)
                ? 'Choose a project status'
                : ''
            "
            :required="true" />

          <YearInput
            v-model:value="yearOfConstruction"
            class="col-span-10 min-w-0 @lg:col-span-5 @3xl:col-span-2"
            label="Year of construction"
            color="primary"
            tooltip="The year construction of the building begins. This helps contextualize the project and may affect applicable standards, assumptions, or emissions data."
            :error-msg="
              props.wasValidated && (yearOfConstruction === null || 2023 > yearOfConstruction)
                ? 'Cannot be before 2023'
                : undefined
            "
            :required="true"
            @new-selected="(year: number) => (yearOfConstruction = year)" />

          <YearInput
            v-model:value="yearOfCommissioning"
            class="col-span-10 min-w-0 @lg:col-span-5 @3xl:col-span-2"
            label="Year of commissioning"
            color="primary"
            markdown-path="YearOfCommissioning"
            :error-msg="
              props.wasValidated && (yearOfCommissioning === null || 2023 > yearOfCommissioning)
                ? 'Cannot be before 2023'
                : undefined
            "
            :required="true"
            @new-selected="handleCommissioningYearSelected" />
        </fieldset>
      </div>

      <!-- Mounted only on demand: Modal.vue's Teleport target doesn't exist in Storybook, so mounting
           unconditionally crashed the story. The id is set before the toggle fires, so it never mounts without one. -->
      <ModalConfirm
        v-if="pendingCommissioningYear !== null"
        :id="RESET_FACTORS_MODAL_ID"
        heading="Change the year of commissioning?"
        message="The emission factors chosen for Installation (A5) and Energy use (B6) depend on this year. Changing it clears them, so they will have to be chosen again."
        @confirm="handleResetConfirmed" />

      <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-4 border border-slate-300">
        <!-- Disclosure, not a heading — five fields shouldn't front-load "just press Next," and closed
             by default is fine; the summary covers it. Native button, not ExpandWrapper/FilterGroupHeader — both are feature-internal. -->
        <button
          type="button"
          class="focus-ring rounded-4 duration-fast ease-standard flex w-full items-center gap-2 py-1 text-left transition-colors hover:bg-slate-100"
          :aria-expanded="optionalDetailsShown"
          aria-controls="project-optional-details"
          @click="optionalDetailsShown = !optionalDetailsShown">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <SlidersHorizontal
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <ChevronDown
            class="icon-sm text-type-muted duration-fast ease-standard shrink-0 transition-transform"
            :class="optionalDetailsShown ? 'rotate-180' : ''"
            :stroke-width="1.5"
            aria-hidden="true" />
          <!-- shrink-0: the caption beside it is the flexible one, the title must never wrap mid-word. -->
          <span class="t-sh4 text-type-default shrink-0 whitespace-nowrap">Optional details</span>
          <!-- Named, not counted: "Optional details" alone gives no reason to open it, and five isn't
               information — the field names are; the badge beside them answers a different question. -->
          <span class="t-caption text-type-muted min-w-0 font-normal"
            >project number, users, builder, client, LCA responsible</span
          >
          <span
            v-if="!optionalDetailsShown && optionalDetailsFilledCount > 0"
            class="t-caption text-app-blue-800 shrink-0 font-normal whitespace-nowrap"
            >{{ optionalDetailsFilledCount }} filled</span
          >
        </button>

        <Transition name="fade-in">
          <fieldset
            v-if="optionalDetailsShown"
            id="project-optional-details"
            class="grid gap-4 sm:grid-cols-2"
            :disabled="isReadOnly">
            <!-- Nothing here is checked by validateProjectDetails — grouped separately so what's required
                 stays visible instead of being carried by an asterisk per label. -->
            <TextInputNew
              v-model="projectNumber"
              label="Project number"
              tooltip="A unique reference number for the project, often used for internal tracking or documentation. Using a consistent project number makes it easier to link this project to external systems and reports." />

            <NumberInput
              v-model:value="numberOfPeople"
              label="Number of persons/users"
              tooltip="The expected number of people using the building during normal operation. This value supports assumptions to calculate the emissions per person for the project."
              color="primary"
              :positive="true"
              :no-decimals="true"
              :required="false" />

            <TextInputNew
              v-model="builder"
              label="Builder name"
              tooltip="The organization or company responsible for constructing the building. This information is used for documentation and project traceability."
              :remove-leading-whitespace="true" />

            <TextInputNew
              v-model="clientName"
              label="Client name"
              tooltip="The client or owner commissioning the project. Including the client name helps ensure clear ownership and accountability for the project data."
              :remove-leading-whitespace="true" />

            <TextInputNew
              v-model="lcaResponsibleName"
              label="LCA Responsible Name"
              tooltip="The person responsible for the life cycle assessment. This identifies who is accountable for data quality, assumptions, and results."
              :remove-leading-whitespace="true" />
          </fieldset>
        </Transition>
      </div>
    </div>
  </div>
</template>
